import React, {
  ComponentType,
  useCallback,
  useMemo,
  useState
} from 'react'
import {
  Button,
  Form,
  Grid,
  Icon,
  Popup,
  Segment
} from 'semantic-ui-react'
import flatten from 'lodash/flatten'
import { DateInput } from 'semantic-ui-calendar-react'
import { connect } from 'react-redux'
// Local
import * as c from '../../constant'
import AppointmentTypeSelect from './appointmentTypeSelect'
import OrganizationSelect from './organizationSelect'
import PatientRegionSelect from './patientRegionSelect'
import PractitionerSelect from './practitionerSelect'
import PotentialCountSelect from './potentialCountSelect'
import SpecialtySelect from './specialtySelect'
import VbcProgramSelect from './vbcProgramSelect'
import NLPConditionSelect from './nlpConditionSelect'
import { User } from '../authenticated/types'
import { generateQueryFilters } from './generateQueryFilters'
import ReveleerButton from './ReveleerButton'
import If from './if'
import { shouldRenderReskin2025 } from '../../constant'
// Styling
import './filter.css'
import { RootState } from '../../store'

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>
const SafeForm = Form as unknown as ComponentType<any>

/**
 * Provides a generic search filter component
 * @param submit Callback on submit button click
 *   Arguments: Object of filter key -> filter value. This object is ready to
 *   send to a search endpoint
 * @param filters Array of search filter metadata. Example:
 *   [
 *     [
 *       { placeholder: "MRN", name: "mrn", width: 8, like: true },
 *       { placeholder: "Last Name", name: "lastName", width: 8, like: true }
 *     ],
 *     [
 *       { placeholder: "Appointment Date", name: "start", width: 8, date: true },
 *       { placeholder: "Date of Birth", name: "birthDate", width: 8, date: true },
 *     ]
 *   ];
 *   Each child array is rendered as a row in the search form. The name
 *   property must match the query map in the corresponding repository. The
 *   width sets the grid column width. The default filter type is text input.
 *   Other supported types are: date: true, practitionerSelect: true,
 *   appointmentTypeSelect: true, organizationSelect: true,
 *   patientRegionSelect: true, vbcProgramSelect: true
 * @param initialValues Object of initial filter values: filter key -> value
 * @param onChange Callback function executed when a filter changes. Note that
 *   this is optional. The filter component is managing its own state
 *   Arguments: filter key, new value
 */

const mapStateToProps = (state: RootState) => ({
  currentUser: state.user
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>

type Props = ReduxStateProps & {
  filters: any[]
  // filters: Array<Record<string, string>[]>
  // initialValues: Record<string, string>
  initialValues: any
  onChange: any
  // onChange: (
  //   (
  //     key: string,
  //     value: string | boolean | null
  //   ) => void
  // ) | undefined
  submit: any
  // submit: (
  //   filterValues: {
  //     key: string,
  //     value: string
  //   },
  // ) => void
  updateUserPatientListFilters: (
    user: User,
    initialValues: Record<string, string>
  ) => void
  setInitialValuesState: (x: any) => void
  onClear: (
    () => void
  ) | undefined
  buttonText: string
}

function FilterComponent(
  props: Props
): JSX.Element {
  const {
    currentUser,
    filters: filtersProp,
    initialValues: initialValuesProp,
    onChange: onChangeProp,
    updateUserPatientListFilters: updateUserPatientListFiltersProp,
    setInitialValuesState: setInitialValuesStateProp,
    onClear: onClearProp,
    buttonText: buttonTextProp,
    submit: submitProp
  } = props
  const initialValues = initialValuesProp || {}
  const initialForm = flatten(filtersProp).map((row: any) => {
    const val = initialValues[row.name]
      ? initialValues[row.name]
      : ''
    return { key: row.name, val }
  }).reduce((accumulator, currentValue) => {
    accumulator[currentValue.key] = currentValue.val
    return accumulator
  }, {} as Record<string, string | string[]>)

  const [
    formState,
    setFormState
  ] = useState<Record<string, string | string[]>>(initialForm)

  const updateField = useCallback((
    e: any,
    { name, value }: {
      name: string,
      value: any
    }
  ) => {
    let formValue: any

    if (typeof value === 'boolean') {
      // Convert boolean/null to string for formState compatibility
      formValue = String(value)
    } else if (value === null) {
      formValue = ''
    } else {
      formValue = value
    }

    setFormState((prevState) => {
      return Object.assign(
        {},
        prevState,
        {
          [name]: formValue
        }
      )
    })
    // Note: onChangeProp is intentionally not called here to prevent
    // re-renders and focus loss. Form state is managed locally and
    // submitted only when the Filter button is clicked.
  }, [])

  const submit = () => {
    if (updateUserPatientListFiltersProp) {
      updateUserPatientListFiltersProp(
        currentUser,
        initialValuesProp as Record<string, string>
      )
    }
    submitProp(
      generateQueryFilters(
        filtersProp,
        formState
      ) as unknown as { key: string, value: string }
    )
  }

  const cancel = () => {
    const emptied: Record<string, string | string[]> = Object.fromEntries(
      Object.keys(formState).map(key => [ key, '' ])
    )
    setFormState(emptied)
    submitProp(
      generateQueryFilters(
        filtersProp,
        emptied
      ) as unknown as { key: string, value: string }
    )
    if (setInitialValuesStateProp) {
      setInitialValuesStateProp(generateQueryFilters(filtersProp, emptied))
    }
    if (updateUserPatientListFiltersProp) {
      // Convert emptied to Record<string, string> for compatibility
      const emptiedString: Record<string, string> = Object.fromEntries(
        Object.entries(emptied).map(([ key, val ]) => [
          key,
          Array.isArray(val)
            ? val[0] || ''
            : val || ''
        ])
      )
      updateUserPatientListFiltersProp(currentUser, emptiedString)
    }
    if (onClearProp) {
      onClearProp()
    }
  }

  const filterFields = useMemo(() => {
    return filtersProp.map((row, rowIndex) => {
      const rowFilters = row.map((filter: Record<string, string>) => {
        let field
        const fieldValue: string | string[] | undefined = formState[filter.name]
        const stringValue: string = Array.isArray(fieldValue)
          ? (fieldValue[0] || '')
          : (fieldValue || '')

        if (filter.date) {
          field = (
            <DateInput
              className="dateinput"
              name={ filter.name }
              placeholder={ filter.placeholder }
              value={ stringValue }
              onChange={ updateField as any }
              dateFormat={ c.FORMAT.DATE }
              autoComplete="off"
            />
          )
        } else if (filter.organizationSelect) {
          field = (
            <OrganizationSelect
              name={ filter.name }
              value={ stringValue }
              onChange={ updateField as any }
              placeholder={ filter.placeholder }
              multiple={ false }
              allOption={ false }
            />
          )
        } else if (filter.practitionerSelect) {
          field = (
            <PractitionerSelect
              name={ filter.name }
              value={ stringValue }
              onChange={ updateField as any }
              allOption={ false }
            />
          )
        } else if (filter.patientRegionSelect) {
          field = (
            <PatientRegionSelect
              name={ filter.name }
              value={ stringValue }
              onChange={ updateField as any }
              allOption={ false }
              placeholder=""
            />
          )
        } else if (filter.appointmentTypeSelect) {
          field = (
            <AppointmentTypeSelect
              name={ filter.name }
              value={Array.isArray(fieldValue) ? fieldValue : []}
              onChange={ updateField as any }
              allOption={ false }
              placeholder=""
              multiple
            />
          )
        } else if (filter.vbcProgramSelect) {
          field = (
            <VbcProgramSelect
              name={ filter.name }
              value={ stringValue }
              onChange={ updateField as any }
              allOption={ false }
            />
          )
        }
        else if (filter.potentialCountSelect) {
          field = (
            <PotentialCountSelect
              name={ filter.name }
              value={ fieldValue }
              onChange={ updateField as any }
            />
          )
        }
        else if (filter.specialtySelect) {
          field = (
            <SpecialtySelect
              name={ filter.name }
              value={ fieldValue }
              onChange={ updateField as any }
            />
          )
        }
        else if (filter.nlpConditionSelect) {
          field = (
            <NLPConditionSelect
              name={ filter.name }
              value={ !!fieldValue }
              onChange={ updateField as any }
              placeholder=""
            />
          )
        }
        else {
          field = (
            <Form.Input
              name={ filter.name }
              placeholder={ filter.placeholder }
              value={ stringValue }
              onChange={ updateField as any }
            />
          )
        }
        return (
          <Grid.Column
            width={ filter.width }
            key={ filter.name }
          >
            { field }
          </Grid.Column>
        )
      })
      return (
        <Grid.Row key={ `filter-row-${rowIndex}` }>
          { rowFilters }
        </Grid.Row>
      )
    })
  }, [ filtersProp, formState, updateField ])

  return (
    <SafeSegment
      attached
      className="no-margin filter"
      id="filter"
    >
      <SafeForm>
        <SafeGrid
          className="filter-grid"
          stackable
        >
          { filterFields }
          <Grid.Row>
            <Grid.Column textAlign="right">
              <span className="filter-submit-button">
                <If isTrue={ !shouldRenderReskin2025 }>
                  <Button
                    onClick={ submit }
                    color="blue"
                    id="filter-button"
                  >
                    { buttonTextProp || 'Filter' }
                  </Button>
                </If>
                <If isTrue={ shouldRenderReskin2025 }>
                  <ReveleerButton
                    buttonLabel={ buttonTextProp || 'Filter' }
                    buttonSize="small"
                    buttonType="primary"
                    id="filter-button"
                    isDisabled={ false }
                    onClick={ submit }
                    type="button"
                  />
                </If>
              </span>
              <span className="filter-clear-button">
                <Popup content="Clear Filters" mouseEnterDelay={ 1000 }
                  trigger={
                    shouldRenderReskin2025
                      ? (
                        <ReveleerButton
                          buttonLabel="Clear"
                          buttonSize="small"
                          buttonType="secondary"
                          id="cancel-clear-filters-button"
                          isDisabled={ false }
                          onClick={ cancel }
                          type="button"
                        />
                      )
                      : (
                        <Button
                          icon
                          id="cancel-clear-filters-button"
                          onClick={ cancel }>
                          <Icon name="cancel" />
                        </Button>
                      )
                  }
                />
              </span>
            </Grid.Column>
          </Grid.Row>
        </SafeGrid>
      </SafeForm>
    </SafeSegment>
  )
}

export default connect(mapStateToProps)(FilterComponent)
